// src/sections/privacy/ZKPARecoveryModal.tsx
//
// Hai chế độ:
//  - "recover": nhập 24 từ bí mật → re-derive MK → /keys/rotate
//  - "reset":   tạo seed phrase mới → derive MK mới → /keys/rotate
//                (⚠ dữ liệu đã mã hoá bằng khoá cũ sẽ không đọc được)
//
// SECURITY RULES:
// 1. Seed phrase chỉ tồn tại trong ref/state component — không gửi lên server.
// 2. Entropy là biến cục bộ — zeroed ngay sau khi dùng.
// 3. Server chỉ nhận ciphertext (encMK, encSK, pubKey).

import { useState, useRef, useEffect } from "react";
import { SvgShieldCheck, SvgClipboard, SvgCheck } from "@opal/icons";
import { generateMnemonic, mnemonicToEntropy, validateMnemonic } from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english.js";
import { useAuthStore } from "@/shared/stores/authStore";
import { useZKPAStore } from "@/shared/stores/zkpaStore";
import { MasterKeyManager } from "@/shared/lib/crypto/masterKey";
import { zkpaApi } from "@/shared/api/zkpa.api";

interface Props {
  open: boolean;
  userId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

type Mode = "recover" | "reset";
type ResetStep = "warn" | "show" | "confirm" | "rotating" | "done";

function toBase64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

// ── Helper: derive, encrypt, rotate ──────────────────────────────────────────
async function doRotate(
  mnemonic: string,
  password: string,
  userId: string,
): Promise<{ publicKeyBytes: Uint8Array }> {
  const entropy = mnemonicToEntropy(mnemonic, wordlist);
  const masterKey = await MasterKeyManager.deriveKeyFromEntropy(new Uint8Array(entropy), userId);
  const { encryptedMasterKey, nonce, pbkdf2Salt } =
    await MasterKeyManager.encryptMasterKey(masterKey, password, userId);

  const sigKeyPair = await crypto.subtle.generateKey({ name: "Ed25519" }, true, ["sign", "verify"]);
  const pubKeyRaw = new Uint8Array(await crypto.subtle.exportKey("raw", sigKeyPair.publicKey));
  const privKeyRaw = new Uint8Array(await crypto.subtle.exportKey("pkcs8", sigKeyPair.privateKey));

  const skNonce = crypto.getRandomValues(new Uint8Array(12));
  const mkRaw = new Uint8Array(await crypto.subtle.exportKey("raw", masterKey));
  const tempMk = await crypto.subtle.importKey("raw", mkRaw, { name: "AES-GCM" }, false, ["encrypt"]);
  mkRaw.fill(0);
  const encSK = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv: skNonce }, tempMk, privKeyRaw)
  );

  await zkpaApi.rotate({
    encrypted_master_key: encryptedMasterKey,
    master_key_nonce: nonce,
    encrypted_private_key: toBase64Url(encSK),
    private_key_nonce: toBase64Url(skNonce),
    public_key: toBase64Url(pubKeyRaw),
    pbkdf2_salt: pbkdf2Salt,
    pbkdf2_iterations: 200_000,
  });

  // Zero sensitive
  entropy.fill(0);
  privKeyRaw.fill(0);
  encSK.fill(0);

  // Load into session memory
  const nonExtractableMk = await crypto.subtle.importKey(
    "raw", new Uint8Array(await crypto.subtle.exportKey("raw", masterKey)),
    { name: "AES-GCM" }, false, ["encrypt", "decrypt"]
  );
  const nonExtractableSk = await crypto.subtle.importKey(
    "pkcs8", new Uint8Array(await crypto.subtle.exportKey("pkcs8", sigKeyPair.privateKey)),
    { name: "Ed25519" }, false, ["sign"]
  );
  await MasterKeyManager.loadFromDerivedKey(nonExtractableMk, nonExtractableSk, sigKeyPair.publicKey, pubKeyRaw);

  return { publicKeyBytes: pubKeyRaw };
}

// ── Shared word grid for 24-word display ─────────────────────────────────────
function SeedGrid({ words }: { words: string[] }) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
      {words.map((word, i) => (
        <div key={i} style={{
          display: "flex", alignItems: "center", gap: "3px",
          backgroundColor: "#fff", borderRadius: "5px", padding: "4px 6px",
          border: "1px solid #e5e7eb",
        }}>
          <span style={{ fontSize: "10px", color: "#9ca3af", minWidth: "16px", textAlign: "right" }}>
            {i + 1}.
          </span>
          <span style={{ fontSize: "12px", fontWeight: 600, color: "#1e293b" }}>{word}</span>
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function ZKPARecoveryModal({ open, userId, onSuccess, onCancel }: Props) {
  const [mode, setMode] = useState<Mode>("recover");

  // ── Password state for users whose sessionPassword was lost on refresh
  const [enteredPassword, setEnteredPassword] = useState("");

  // ── Recover state
  const [recoverWords, setRecoverWords] = useState<string[]>(Array(24).fill(""));
  const [recoverError, setRecoverError] = useState<string | null>(null);
  const [recoverLoading, setRecoverLoading] = useState(false);

  // ── Reset state
  const [resetStep, setResetStep] = useState<ResetStep>("warn");
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmWords, setConfirmWords] = useState<string[]>(Array(24).fill(""));
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const newMnemonicRef = useRef<string>("");

  const sessionPassword = useAuthStore((s) => s.sessionPassword);
  const setSessionPassword = useAuthStore((s) => s.setSessionPassword);
  const setKeyState = useZKPAStore((s) => s.setKeyState);

  // Generate new mnemonic when entering reset/show step
  useEffect(() => {
    if (mode === "reset" && resetStep === "show" && !newMnemonicRef.current) {
      newMnemonicRef.current = generateMnemonic(wordlist, 256);
    }
  }, [mode, resetStep]);

  // Clear mnemonic when modal closes or mode changes
  useEffect(() => {
    if (!open) {
      newMnemonicRef.current = "";
      setMode("recover");
      setResetStep("warn");
      setRecoverWords(Array(24).fill(""));
      setRecoverError(null);
      setConfirmWords(Array(24).fill(""));
      setConfirmError(null);
      setEnteredPassword("");
    }
  }, [open]);

  if (!open) return null;

  // Use sessionPassword if available, otherwise fallback to manually entered password
  const activePassword = sessionPassword || enteredPassword;
  const newWords = newMnemonicRef.current ? newMnemonicRef.current.split(" ") : [];

  // ── Recover handlers ────────────────────────────────────────────────────────
  function handleRecoverWordChange(idx: number, val: string) {
    const t = val.trim().toLowerCase();
    setRecoverWords((p) => { const n = [...p]; n[idx] = t; return n; });
    setRecoverError(null);
  }

  function handleRecoverPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData("text").trim().split(/\s+/);
    if (pasted.length === 24) {
      e.preventDefault();
      setRecoverWords(pasted.map((w) => w.toLowerCase()));
    }
  }

  async function handleRecover(e: React.FormEvent) {
    e.preventDefault();
    const mnemonic = recoverWords.join(" ").trim();
    if (!validateMnemonic(mnemonic, wordlist)) {
      setRecoverError("Cụm từ khôi phục không hợp lệ. Vui lòng kiểm tra lại 24 từ.");
      return;
    }
    if (!activePassword) { 
      setRecoverError("Vui lòng nhập mật khẩu đăng nhập để mã hoá khoá bảo mật."); 
      return; 
    }
    
    setRecoverLoading(true);
    try {
      const { publicKeyBytes } = await doRotate(mnemonic, activePassword, userId);
      setSessionPassword(activePassword);
      setKeyState({ status: "ready", publicKeyBytes });
      onSuccess();
    } catch (err: any) {
      console.error("[ZKPA Recovery]", err?.message);
      setRecoverError("Khôi phục thất bại. Mật khẩu không đúng hoặc lỗi mạng.");
    } finally {
      setRecoverLoading(false);
    }
  }

  // ── Reset handlers ─────────────────────────────────────────────────────────
  function handleCopyNewSeed() {
    navigator.clipboard.writeText(newMnemonicRef.current);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  }

  function handleConfirmWordChange(idx: number, val: string) {
    const t = val.trim().toLowerCase();
    setConfirmWords((p) => { const n = [...p]; n[idx] = t; return n; });
    setConfirmError(null);
  }

  function handleConfirmPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData("text").trim().split(/\s+/);
    if (pasted.length === 24) {
      e.preventDefault();
      setConfirmWords(pasted.map((w) => w.toLowerCase()));
    }
  }

  async function handleConfirmAndRotate(e: React.FormEvent) {
    e.preventDefault();
    const typed = confirmWords.join(" ").trim();
    if (typed !== newMnemonicRef.current.trim()) {
      setConfirmError("Cụm từ không khớp. Vui lòng kiểm tra lại thứ tự và chính tả.");
      return;
    }
    if (!activePassword) { 
      setConfirmError("Vui lòng nhập mật khẩu đăng nhập để mã hoá khoá bảo mật."); 
      return; 
    }

    setResetLoading(true);
    setResetStep("rotating");
    try {
      const { publicKeyBytes } = await doRotate(newMnemonicRef.current, activePassword, userId);
      newMnemonicRef.current = "";
      setSessionPassword(activePassword);
      setKeyState({ status: "ready", publicKeyBytes });
      setResetStep("done");
    } catch (err: any) {
      console.error("[ZKPA Reset]", err?.message);
      setResetError("Tạo khoá mới thất bại. Mật khẩu không đúng hoặc lỗi mạng.");
      setResetStep("confirm");
    } finally {
      setResetLoading(false);
    }
  }

  // ── Styles ─────────────────────────────────────────────────────────────────
  const S = {
    overlay: {
      position: "fixed" as const, inset: 0, zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center",
      backgroundColor: "rgba(0,0,0,0.75)",
    },
    card: {
      backgroundColor: "#ffffff", color: "#111827",
      borderRadius: "12px", boxShadow: "0 25px 50px rgba(0,0,0,0.4)",
      width: "100%", maxWidth: "780px", margin: "0 16px",
      padding: "24px", maxHeight: "92vh", overflowY: "auto" as const, overflowX: "hidden" as const,
    },
    tabBar: {
      display: "flex", gap: "0", marginBottom: "20px",
      border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden",
    },
    tab: (active: boolean): React.CSSProperties => ({
      flex: 1, padding: "9px 12px", textAlign: "center",
      fontSize: "13px", fontWeight: 600, cursor: "pointer", border: "none",
      backgroundColor: active ? "#2563eb" : "#f9fafb",
      color: active ? "#fff" : "#6b7280",
      transition: "background 0.15s, color 0.15s",
    }),
    btnPrimary: (disabled = false): React.CSSProperties => ({
      flex: 2, padding: "10px", border: "none", borderRadius: "8px",
      fontSize: "14px", fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
      background: disabled ? "#c7d2fe" : "#4f46e5", color: "#fff",
    }),
    btnSecondary: (): React.CSSProperties => ({
      flex: 1, padding: "10px", borderRadius: "8px", fontSize: "14px",
      fontWeight: 500, cursor: "pointer", background: "#f3f4f6",
      color: "#374151", border: "1px solid #d1d5db",
    }),
    btnDanger: (disabled = false): React.CSSProperties => ({
      flex: 2, padding: "10px", border: "none", borderRadius: "8px",
      fontSize: "14px", fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
      background: disabled ? "#fca5a5" : "#dc2626", color: "#fff",
    }),
    hint: (color = "#1e40af", bg = "#eff6ff", border = "#bfdbfe"): React.CSSProperties => ({
      backgroundColor: bg, border: `1px solid ${border}`, borderRadius: "8px",
      padding: "10px 14px", marginBottom: "16px", fontSize: "13px", color,
    }),
    error: (): React.CSSProperties => ({
      backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px",
      padding: "10px 14px", marginBottom: "14px", fontSize: "13px", color: "#dc2626",
    }),
    wordInput: (filled: boolean): React.CSSProperties => ({
      flex: 1, minWidth: 0, boxSizing: "border-box" as const,
      border: `1px solid ${filled ? "#6366f1" : "#d1d5db"}`,
      borderRadius: "5px", padding: "4px 5px", fontSize: "12px",
      backgroundColor: filled ? "#f5f3ff" : "#f9fafb", color: "#111827", outline: "none",
    }),
  };

  // ── Word input grid ─────────────────────────────────────────────────────────
  function WordInputGrid({
    words, onChange, onPaste, firstPlaceholder = "dán...",
  }: {
    words: string[];
    onChange: (i: number, v: string) => void;
    onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
    firstPlaceholder?: string;
  }) {
    return (
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mb-4">
        {words.map((word, idx) => (
          <div key={idx} style={{ display: "flex", alignItems: "center", gap: "3px" }}>
            <span style={{ fontSize: "10px", color: "#9ca3af", minWidth: "16px", textAlign: "right", flexShrink: 0 }}>
              {idx + 1}.
            </span>
            <input
              id={`word-input-${idx + 1}`}
              type="text"
              autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false}
              value={word}
              onChange={(e) => onChange(idx, e.target.value)}
              onPaste={onPaste}
              style={S.wordInput(!!word)}
              placeholder={idx === 0 ? firstPlaceholder : ""}
            />
          </div>
        ))}
      </div>
    );
  }

  // ── Progress bar ────────────────────────────────────────────────────────────
  function Progress({ words }: { words: string[] }) {
    const filled = words.filter((w) => w.length > 0).length;
    const done = filled === 24;
    return (
      <div style={{ marginBottom: "14px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#6b7280", marginBottom: "5px" }}>
          <span>Đã nhập</span>
          <span style={{ fontWeight: 600, color: done ? "#16a34a" : "#374151" }}>{filled} / 24 từ</span>
        </div>
        <div style={{ height: "4px", backgroundColor: "#e5e7eb", borderRadius: "99px", overflow: "hidden" }}>
          <div style={{
            height: "100%", width: `${(filled / 24) * 100}%`,
            backgroundColor: done ? "#16a34a" : "#6366f1",
            borderRadius: "99px", transition: "width 0.15s",
          }} />
        </div>
      </div>
    );
  }

  const recoverAllFilled = recoverWords.every((w) => w.length > 0);
  const confirmAllFilled = confirmWords.every((w) => w.length > 0);

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div id="zkpa-recovery-modal" role="dialog" aria-modal="true"
      aria-labelledby="zkpa-recovery-title" style={S.overlay}>
      <div style={S.card}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
          <SvgShieldCheck style={{ width: "24px", height: "24px", color: "#2563eb", flexShrink: 0 }} aria-hidden="true" />
          <h2 id="zkpa-recovery-title" style={{ fontSize: "18px", fontWeight: 700, margin: 0, color: "#111827" }}>
            Khôi phục khoá bảo mật
          </h2>
        </div>

        {/* ── Success (shared) ─────────────────────────────────────────────── */}
        {(mode === "recover" && !recoverLoading && recoverWords.every(w => w) && false) ||
          (mode === "reset" && resetStep === "done") ? (
          <div style={{ textAlign: "center", padding: "32px 0" }}>
            <div style={{ fontSize: "56px", marginBottom: "16px" }}>🔐</div>
            <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "15px" }}>
              Khoá bảo mật đã được {mode === "recover" ? "khôi phục" : "tạo mới"} thành công.
            </p>
            <button onClick={onSuccess} style={{
              padding: "10px 24px", background: "#2563eb", color: "#fff",
              border: "none", borderRadius: "8px", fontSize: "15px", fontWeight: 600,
              cursor: "pointer", width: "100%",
            }}>Tiếp tục sử dụng</button>
          </div>
        ) : mode === "reset" && resetStep === "done" ? null : (
          <>
            {/* Tab bar */}
            {(mode === "recover" || (mode === "reset" && resetStep === "warn")) && (
              <div style={S.tabBar}>
                <button
                  id="tab-recover"
                  type="button"
                  style={S.tab(mode === "recover")}
                  onClick={() => { setMode("recover"); setRecoverError(null); }}
                >
                  🔑 Khôi phục bằng 24 từ
                </button>
                <button
                  id="tab-reset"
                  type="button"
                  style={S.tab(mode === "reset")}
                  onClick={() => { setMode("reset"); setResetStep("warn"); setResetError(null); }}
                >
                  ✨ Tạo khoá mới
                </button>
              </div>
            )}

            {/* ── MODE: RECOVER ──────────────────────────────────────────────── */}
            {mode === "recover" && (
              recoverLoading
                ? <Spinner label="Đang khôi phục khoá bảo mật..." />
                : (
                  <form onSubmit={handleRecover}>
                    <div style={S.hint()}>
                      💡 Nhập đúng <strong>24 từ</strong> theo thứ tự khi thiết lập.
                      Bạn có thể <strong>dán toàn bộ</strong> vào ô số 1.
                    </div>
                    <WordInputGrid
                      words={recoverWords}
                      onChange={handleRecoverWordChange}
                      onPaste={handleRecoverPaste}
                    />
                    <Progress words={recoverWords} />
                    
                    {!sessionPassword && (
                      <div style={{ marginBottom: "16px" }}>
                        <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "5px", color: "#374151" }}>
                          Mật khẩu đăng nhập (để mã hoá khoá mới)
                        </label>
                        <input
                          type="password"
                          required
                          value={enteredPassword}
                          onChange={(e) => setEnteredPassword(e.target.value)}
                          style={{
                            width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", 
                            borderRadius: "8px", padding: "10px 14px", fontSize: "14px",
                            backgroundColor: "#f9fafb", outline: "none",
                          }}
                          placeholder="••••••••"
                        />
                      </div>
                    )}

                    {recoverError && <div style={S.error()}>⚠️ {recoverError}</div>}
                    <div style={{ display: "flex", gap: "10px" }}>
                      <button type="button" id="recover-cancel-btn" onClick={onCancel} style={S.btnSecondary()}>← Quay lại</button>
                      <button type="submit" id="recover-submit-btn"
                        disabled={recoverLoading || !recoverAllFilled}
                        style={S.btnPrimary(recoverLoading || !recoverAllFilled)}>
                        {recoverAllFilled ? "🔑 Khôi phục khoá" : "Nhập đủ 24 từ để tiếp tục"}
                      </button>
                    </div>
                  </form>
                )
            )}

            {/* ── MODE: RESET ────────────────────────────────────────────────── */}
            {mode === "reset" && (
              <>
                {/* Step: warn */}
                {resetStep === "warn" && (
                  <div>
                    <div style={{ ...S.hint("#92400e", "#fef3c7", "#fde68a"), marginBottom: "20px" }}>
                      ⚠️ <strong>Cảnh báo quan trọng:</strong> Tạo khoá mới sẽ <strong>thay thế hoàn toàn</strong> khoá bảo mật hiện tại.
                      Tất cả dữ liệu đã mã hoá bằng khoá cũ sẽ <strong>không thể đọc được nữa</strong>.
                      Chỉ thực hiện nếu bạn không thể khôi phục khoá cũ.
                    </div>
                    <div style={{ display: "flex", gap: "10px" }}>
                      <button type="button" id="reset-cancel-btn" onClick={onCancel} style={S.btnSecondary()}>← Quay lại</button>
                      <button type="button" id="reset-proceed-btn"
                        onClick={() => setResetStep("show")}
                        style={S.btnDanger()}>
                        Tôi hiểu — Tạo khoá mới
                      </button>
                    </div>
                  </div>
                )}

                {/* Step: show new seed */}
                {resetStep === "show" && (
                  <div>
                    <div style={S.hint("#1e40af", "#eff6ff", "#bfdbfe")}>
                      🔐 Đây là <strong>24 từ bí mật mới</strong> của bạn. Hãy ghi ra giấy hoặc lưu vào nơi an toàn
                      trước khi tiếp tục. Không ai có thể giúp bạn nếu mất chúng.
                    </div>
                    {newWords.length === 24 && <SeedGrid words={newWords} />}
                    <button
                      id="reset-copy-btn"
                      type="button"
                      onClick={handleCopyNewSeed}
                      style={{
                        width: "100%", padding: "8px", margin: "12px 0",
                        background: "#f3f4f6", color: "#374151",
                        border: "1px solid #d1d5db", borderRadius: "8px",
                        fontSize: "13px", fontWeight: 500, cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                      }}
                    >
                      {copied
                        ? <><SvgCheck style={{ width: "14px", height: "14px" }} />Đã sao chép!</>
                        : <><SvgClipboard style={{ width: "14px", height: "14px" }} />Sao chép 24 từ</>
                      }
                    </button>
                    <div style={{ display: "flex", gap: "10px" }}>
                      <button type="button" id="reset-back-show-btn"
                        onClick={() => setResetStep("warn")} style={S.btnSecondary()}>← Quay lại</button>
                      <button type="button" id="reset-saved-btn"
                        onClick={() => setResetStep("confirm")}
                        style={{ ...S.btnPrimary(), flex: 2 }}>
                        Tôi đã lưu — Xác nhận ✓
                      </button>
                    </div>
                  </div>
                )}

                {/* Step: confirm by re-entering */}
                {resetStep === "confirm" && (
                  <form onSubmit={handleConfirmAndRotate}>
                    <div style={S.hint()}>
                      ✍️ Nhập lại <strong>24 từ</strong> vừa lưu để xác nhận. Bạn có thể dán toàn bộ vào ô số 1.
                    </div>
                    <WordInputGrid
                      words={confirmWords}
                      onChange={handleConfirmWordChange}
                      onPaste={handleConfirmPaste}
                      firstPlaceholder="dán hoặc nhập..."
                    />
                    <Progress words={confirmWords} />

                    {!sessionPassword && (
                      <div style={{ marginBottom: "16px" }}>
                        <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "5px", color: "#374151" }}>
                          Mật khẩu đăng nhập (để mã hoá khoá mới)
                        </label>
                        <input
                          type="password"
                          required
                          value={enteredPassword}
                          onChange={(e) => setEnteredPassword(e.target.value)}
                          style={{
                            width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", 
                            borderRadius: "8px", padding: "10px 14px", fontSize: "14px",
                            backgroundColor: "#f9fafb", outline: "none",
                          }}
                          placeholder="••••••••"
                        />
                      </div>
                    )}

                    {confirmError && <div style={S.error()}>⚠️ {confirmError}</div>}
                    {resetError && <div style={S.error()}>⚠️ {resetError}</div>}
                    <div style={{ display: "flex", gap: "10px" }}>
                      <button type="button" id="reset-back-confirm-btn"
                        onClick={() => { setResetStep("show"); setConfirmError(null); setConfirmWords(Array(24).fill("")); }}
                        style={S.btnSecondary()}>← Quay lại</button>
                      <button type="submit" id="reset-confirm-btn"
                        disabled={resetLoading || !confirmAllFilled}
                        style={S.btnDanger(resetLoading || !confirmAllFilled)}>
                        {confirmAllFilled ? "🔑 Xác nhận & Tạo khoá mới" : "Nhập đủ 24 từ để tiếp tục"}
                      </button>
                    </div>
                  </form>
                )}

                {/* Step: rotating spinner */}
                {resetStep === "rotating" && <Spinner label="Đang tạo khoá bảo mật mới..." />}

                {/* Step: done */}
                {resetStep === "done" && (
                  <div style={{ textAlign: "center", padding: "32px 0" }}>
                    <div style={{ fontSize: "56px", marginBottom: "16px" }}>🔐</div>
                    <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "15px" }}>
                      Khoá bảo mật mới đã được tạo và cập nhật thành công.
                    </p>
                    <button id="reset-done-btn" onClick={onSuccess} style={{
                      padding: "10px 24px", background: "#2563eb", color: "#fff",
                      border: "none", borderRadius: "8px", fontSize: "15px",
                      fontWeight: 600, cursor: "pointer", width: "100%",
                    }}>Tiếp tục sử dụng</button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Spinner ───────────────────────────────────────────────────────────────────
function Spinner({ label }: { label: string }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 0" }} role="status" aria-live="polite">
      <div
        className="animate-spin"
        style={{
          width: "44px", height: "44px",
          border: "3px solid #2563eb", borderTopColor: "transparent",
          borderRadius: "50%", margin: "0 auto 20px",
        }}
        aria-hidden="true"
      />
      <p style={{ color: "#6b7280", fontSize: "14px" }}>{label}</p>
    </div>
  );
}
